/**
 * L'EFFIGIE — la tête de la bête, dessinée ici (design §3).
 *
 * ELLE EXISTE PARCE QU'ON JOUE SANS IMAGES. Le §6 du brief fait des illustrations un poste manuel :
 * la plupart des aventures n'en auront aucune pendant longtemps, et l'on frappait donc un nom
 * pendant six tours. Un bandeau de nœud absent ne se remarque pas — le texte décrit le lieu ; un
 * adversaire sans visage, si.
 *
 * CE N'EST PAS UN PLACEHOLDER. Pas de silhouette grise, pas de point d'interrogation, pas d'icône
 * de bibliothèque (§1.3) : la même plume que la carte, les mêmes hachures, la même encre. Six
 * familles — quadrupède, humanoïde, essaim, machine, informe, colosse — qu'on reconnaît avant de
 * lire le nom, et qui se choisissent dans `bestiaire/effigie.ts`, où la décision se teste.
 *
 * UNE ILLUSTRATION POSÉE LA REMPLACE, toujours et au même endroit : c'est `Illustration` qui passe
 * en premier, et l'effigie ne se dessine que si `url` est vide.
 *
 * ELLE NE DIT QUE CE QUI EST DÉJÀ AFFICHÉ : l'espèce, l'arme, l'état qu'un coup inflige, la masse
 * de points de vie. Le serveur ne rend pas les caractéristiques d'un adversaire ; l'effigie ne les
 * ferait pas entrer par le dessin.
 */
import type { ReactElement } from "react";

import { effigieDe, type Effigie as Traits, type Signalement } from "../bestiaire/effigie.js";
import { t } from "../lexique/langue.js";

/** Les yeux : deux pour ce qui a un visage, davantage pour ce qui n'en a pas. Le nombre est tiré de
 * la graine — donc stable pour une espèce, et différent d'une espèce à l'autre. */
function Yeux({ points, r = 2.6 }: { points: [number, number][]; r?: number }) {
  return (
    <>
      {points.map(([x, y], i) => (
        <circle key={i} className="effigie__oeil" cx={x} cy={y} r={r} />
      ))}
    </>
  );
}

/**
 * L'ARME EST TENUE, PAS POSÉE À CÔTÉ. Première version : un accent dessiné en coordonnées absolues,
 * les mêmes pour les six familles — d'où une lame qui flottait au-dessus de l'épaule d'un loup.
 * Chaque famille place donc son armement elle-même, là où le membre se termine. C'est plus de code
 * et c'est le prix : une arme qui ne touche pas la bête n'est pas son arme.
 */
type Corps = (traits: Traits) => ReactElement;

const Quadrupede: Corps = ({ graine, armement }) => (
  <>
    {/* Le tronc : l'échine tendue, la croupe haute, le ventre remonté. Une bête qui chasse est une
        bête qui n'est pas ronde. */}
    <path
      className="effigie__masse"
      d="M33 47C43 38 65 38 75 48c6 6 7 14 3 20-8 7-34 7-42 1-6-5-8-16-3-22z"
    />
    {/* L'encolure et la tête : plus bas que l'échine, tendues vers l'avant, le museau détaché du
        tronc. Tant que la tête touchait le corps, on dessinait un scarabée. */}
    <path className="effigie__masse" d="M34 52c-4-6-12-9-19-6-7 3-10 10-7 15 2 4 6 5 10 5h4l3 7 3-8c6-2 9-8 6-13z" />
    <path className="effigie__trait effigie__trait--fin" d="M9 62h12" />
    {/* Les oreilles : deux, sur la tête, jamais parallèles. */}
    <path className="effigie__trait" d="M18 47l-1-11 9 6M29 45l4-10 6 8" />
    {/* Quatre pattes : deux d'appui, deux en mouvement — c'est ce qui fait marcher un dessin. */}
    <path className="effigie__trait" d="M38 69l-4 18M48 71v16M66 71l4 16M74 67l6 20" />
    {/* La queue est de la MASSE, pas un trait : un trait d'un pixel et demi disparaît à cinquante
        pixels de large, c'est-à-dire à la taille où l'effigie est réellement affichée. */}
    <path
      className="effigie__masse"
      d={
        graine % 2 === 0
          ? "M74 54c9-2 14-9 14-18l5 1c0 12-7 20-17 22z"
          : "M75 60c10 2 14 9 14 19l-5 1c-1-8-5-13-12-15z"
      }
    />
    {/* L'échine, en deux traits de plume par-dessus le lavis. */}
    <path className="effigie__trait effigie__trait--fin" d="M40 46c8-4 22-4 30 2M44 62h24" />
    <Yeux points={[[17, 53]]} r={2.4} />
    {armement === "griffes" ? (
      <path className="effigie__trait" d="M32 87l-4 6M42 88l-3 6M70 87l4 6M79 88l3 6" />
    ) : null}
    {armement === "crocs" ? <path className="effigie__trait effigie__trait--fin" d="M9 62l2 5 2-5M14 62l2 5 2-5" /> : null}
  </>
);

const Humanoide: Corps = ({ graine, armement }) => {
  const capuche = graine % 3 !== 0;
  return (
    <>
      {/* La robe : elle s'évase et touche le sol. Un humanoïde à jambes fines devient un pantin. */}
      <path className="effigie__masse" d="M42 46h18c7 8 12 24 11 42H31c-1-18 4-34 11-42z" />
      {capuche ? (
        <>
          <path className="effigie__masse" d="M41 47c-1-13 4-23 12-23s13 10 12 23c-5 5-19 5-24 0z" />
          {/* L'ombre de la capuche : c'est elle qui fait qu'on ne voit pas le visage, et c'est le
              seul endroit du dessin où l'encre est pleine. */}
          <path className="effigie__creux" d="M45 40c0-7 3-11 8-11s8 4 8 11c-3 4-13 4-16 0z" />
        </>
      ) : (
        <>
          <path className="effigie__masse" d="M53 20a11 11 0 1 1 0 22 11 11 0 0 1 0-22z" />
          <path className="effigie__trait effigie__trait--fin" d="M46 42l2 5M60 42l-2 5" />
        </>
      )}
      {/* Les bras : l'un le long du corps, l'autre replié — celui qui tient. */}
      <path className="effigie__trait" d="M42 52c-7 6-10 15-9 24" />
      <path className="effigie__trait" d="M64 52c8 4 13 10 14 17" />
      <Yeux points={capuche ? [[49, 38], [57, 38]] : [[49, 29], [57, 29]]} r={capuche ? 1.7 : 1.8} />
      {/* CE QU'IL TIENT, au bout du bras droit — en (78, 69). */}
      {armement === "lame" ? <path className="effigie__trait" d="M78 69L94 34M86 44l7 3-2 7" /> : null}
      {armement === "jet" ? <path className="effigie__trait" d="M78 69c9-4 13-12 11-22M87 45l5 2-2 5" /> : null}
      {armement === "masse" ? <path className="effigie__trait" d="M78 69l10-18M92 43a7 7 0 1 1-.1 0M88 38l3-5M95 44l5-2" /> : null}
      {armement === "griffes" ? <path className="effigie__trait" d="M78 71l5 7M82 68l6 6M86 65l6 5" /> : null}
      {armement === "dard" ? <path className="effigie__trait" d="M78 69l12 12-9-1z" /> : null}
      {armement === "crocs" ? <path className="effigie__trait effigie__trait--fin" d="M48 44l2 4 2-4M55 44l2 4 2-4" /> : null}
    </>
  );
};

const Essaim: Corps = ({ graine, armement }) => {
  // Les positions viennent de la graine : un essaim régulier serait un motif, pas un essaim.
  const bestioles: [number, number, number][] = [];
  let x = graine >>> 0;
  for (let i = 0; i < 15; i++) {
    x = (Math.imul(x ^ (x >>> 13), 0x5bd1e995) >>> 0) || 1;
    const angle = ((x >>> 8) % 360) * (Math.PI / 180);
    const rayon = 6 + ((x >>> 3) % 32);
    bestioles.push([50 + Math.cos(angle) * rayon, 50 + Math.sin(angle) * rayon * 0.85, 2.4 + ((x >>> 17) % 3)]);
  }
  return (
    <>
      {/* La spirale : ce qui fait peur dans un essaim, c'est le mouvement d'ensemble, pas
          l'insecte. Elle est tracée en pointillé, comme la trace d'un vol. */}
      <path
        className="effigie__nuee"
        d="M50 14c22 0 36 16 36 36S70 84 50 84 18 70 18 50s12-28 30-28 26 12 26 26-10 22-22 22-18-8-18-18 6-14 14-14"
      />
      {bestioles.map(([cx, cy, r], i) => (
        <g key={i}>
          {/* Chaque bestiole : un corps, deux ailes. En dessous de trois traits, c'est une tache. */}
          <ellipse className="effigie__masse" cx={cx} cy={cy} rx={r} ry={r * 0.62} transform={`rotate(${(i * 37) % 60 - 30} ${cx} ${cy})`} />
          <path className="effigie__trait effigie__trait--fin" d={`M${cx - 1} ${cy - r * 0.6}l-4-4M${cx + 1} ${cy - r * 0.6}l4-4`} />
          {armement === "dard" && i % 4 === 0 ? (
            <path className="effigie__trait effigie__trait--fin" d={`M${cx + r} ${cy}l5 2-5 2z`} />
          ) : null}
        </g>
      ))}
    </>
  );
};

const Machine: Corps = ({ graine }) => (
  <>
    {/* Tout est droit, sauf ce qui est cassé : une machine de ce monde-ci est une machine qui a
        duré trop longtemps. */}
    <path className="effigie__masse" d="M33 36h34v32H33z" />
    <path className="effigie__masse" d="M41 20h18v16H41z" />
    {/* Les membres : segments et pivots, jamais une courbe. */}
    <path className="effigie__trait" d="M33 42H21l-5 12M67 42h12l6 10 8-4" />
    <path className="effigie__trait" d="M43 68v12l-5 9M58 68v12l6 9" />
    {graine % 2 === 1 ? <path className="effigie__trait" d="M50 20v-8M50 12l-6-4M50 12l6-4" /> : null}
    {/* L'optique : un seul œil, cerclé. On sait tout de suite que ça regarde. */}
    <circle className="effigie__trait" cx="50" cy="28" r="5" />
    <circle className="effigie__oeil" cx="50" cy="28" r="2.2" />
    {/* Les tôles, et la fêlure : c'est la fêlure qui la rend vieille. */}
    <path className="effigie__trait effigie__trait--fin" d="M38 46h24M38 56h14M62 52l4 10-6 4" />
  </>
);

const Informe: Corps = ({ graine }) => {
  const yeux: [number, number][] = [];
  let x = (graine ^ 0x2545f491) >>> 0;
  for (let i = 0; i < 3 + (graine % 4); i++) {
    x = (Math.imul(x ^ (x >>> 15), 0x2c1b3c6d) >>> 0) || 1;
    yeux.push([32 + ((x >>> 5) % 38), 28 + ((x >>> 13) % 30)]);
  }
  return (
    <>
      {/* Une masse sans silhouette stable : le contour est tremblé, le bas se défait en filaments,
          rien n'est symétrique. C'est le seul dessin du bestiaire qui n'a ni tête ni membres. */}
      <path
        className="effigie__masse effigie__masse--pale"
        d="M27 44c-3-14 9-25 24-25 14 0 25 9 25 23 0 8-3 12-2 18 1 8-5 13-13 12-6-1-9-4-14-4s-8 5-15 3c-7-2-9-7-6-14 2-6-1-9 1-13z"
      />
      {/* Les filaments : ils traînent, ils ne pendent pas. */}
      <path className="effigie__trait effigie__trait--fin" d="M31 70c-2 6-1 11 2 16M43 74c-1 7-3 11-1 15M57 74c1 6 3 10 2 14M69 69c3 6 4 12 3 17" />
      {/* Les volutes qui s'en échappent : une chose informe déborde de son contour. */}
      <path className="effigie__nuee" d="M22 40c-6-4-7-12-2-17M78 38c7-3 9-11 5-17" />
      <Yeux points={yeux} r={3.2} />
    </>
  );
};

const Colosse: Corps = ({ graine, armement }) => (
  <>
    {/* Les épaules mangent le cadre, la tête est petite : c'est tout ce qu'il faut pour dire qu'une
        chose est énorme sans avoir de repère à côté d'elle. */}
    <path className="effigie__masse" d="M22 54c3-13 14-20 28-20s25 7 28 20c3 12 3 24 1 34H21c-2-10-2-22 1-34z" />
    <path
      className="effigie__masse"
      d={graine % 2 === 0 ? "M41 34c0-9 4-14 9-14s9 5 9 14z" : "M41 30a9 9 0 1 1 18 0 9 9 0 0 1-18 0z"}
    />
    {/* Les bras tombent plus bas que les hanches : c'est la proportion qui fait le monstre. */}
    <path className="effigie__trait" d="M22 58L11 78l4 8M78 58l11 20-4 8" />
    <path className="effigie__trait effigie__trait--fin" d="M36 88V74M64 88V74M34 52h32" />
    <Yeux points={[[46, 29], [55, 29]]} r={2} />
    {armement === "masse" ? <path className="effigie__trait" d="M15 86L8 62M8 60a7 7 0 1 1 .1 0M4 55l-3-5M13 54l5-3" /> : null}
    {armement === "lame" ? <path className="effigie__trait" d="M85 86L96 52M90 60l6 2" /> : null}
    {armement === "griffes" ? <path className="effigie__trait" d="M13 88l-4 7M18 90l-2 7M85 88l4 7M90 90l2 7" /> : null}
  </>
);

/** Le stigmate : ce qu'un coup porté peut laisser. Une goutte, un suintement — jamais une couleur
 * seule (accessibilité §5), et jamais un chiffre : la probabilité d'un état ne se montre pas. */
function Stigmate({ etat }: { etat: string | null }) {
  if (etat === "poison")
    return <path className="effigie__trait effigie__trait--fin" d="M24 76c0 4 2 6 2 9M32 80c0 3 2 5 2 8" />;
  if (etat === "saignement")
    return <path className="effigie__masse effigie__masse--tache" d="M26 82c2 4 3 6 3 8a3 3 0 0 1-6 0c0-2 1-4 3-8z" />;
  if (etat === "terrifie" || etat === "aveugle")
    return <path className="effigie__nuee" d="M10 26c5-6 10-9 16-10M90 26c-5-6-10-9-16-10" />;
  return null;
}

const CORPS = {
  quadrupede: Quadrupede,
  humanoide: Humanoide,
  essaim: Essaim,
  machine: Machine,
  informe: Informe,
  colosse: Colosse
} as const;

export function Effigie({
  signalement,
  tombe = false,
  taille = 96,
  className
}: {
  signalement: Signalement;
  /** À terre : l'encre pâlit et la bête bascule. On ne relit pas une jauge pour savoir si l'on gagne. */
  tombe?: boolean;
  taille?: number;
  className?: string;
}) {
  const traits = effigieDe(signalement);
  const classes = ["effigie", tombe ? "effigie--tombe" : "", className ?? ""].filter(Boolean).join(" ");

  return (
    <svg
      className={classes}
      width={taille}
      height={taille}
      viewBox="0 0 100 100"
      role="img"
      aria-label={t("{nom}, dessinée à la plume", { nom: signalement.nom })}
    >
      {/* Le champ : le fond de parchemin sur lequel la bête est tirée, et son ombre portée. Sans
          l'ombre, une silhouette flotte au milieu d'un carré. */}
      <circle className="effigie__champ" cx="50" cy="50" r="47" />
      <ellipse className="effigie__sol" cx="50" cy="88" rx="30" ry="4" />
      {/* L'ampleur : ce qui a trois fois plus de points de vie remplit son cadre. C'est la seule
          chose que l'effigie dit des chiffres, et elle est déjà écrite sous le nom. */}
      <g transform={`translate(50 56) scale(${traits.ampleur.toFixed(3)}) translate(-50 -56)`}>
        {CORPS[traits.famille](traits)}
        <Stigmate etat={traits.stigmate} />
      </g>
    </svg>
  );
}
